import { useAuth0 } from '@auth0/auth0-react';
import { useLanguageSelection } from '@dap/common/data-access';
import { Sidebar, Header } from '@dap/common/modules';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { mgprisHeaderHeight } from '@shared/styles';
import { Box, useTheme } from '@mui/material';
import { LanguageCode } from '@shared/types';
import { useUnsubscribeFromQueryParams } from '@dap/notifications/data-access';
import { usePostModalParams } from '@dap/social-feed/utils';
import { lazy, Suspense } from 'react';

// Lazy load SocialFeedPostModal
const SocialFeedPostModal = lazy(() =>
  import('@dap/social-feed/containers').then((module) => ({ default: module.SocialFeedPostModal }))
);

interface Props {
  children: React.ReactNode;
}

export default function AppLayout({ children }: Props) {
  const { switchLanguage, language } = useLanguageSelection();
  const { logout } = useAuth0();
  const { pathname } = useLocation();
  const { breakpoints } = useTheme();
  const { postId, commentKey, closePost } = usePostModalParams();
  useUnsubscribeFromQueryParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Box display="flex" flexGrow="1" id="box">
      <Header
        switchLanguage={switchLanguage}
        language={language as LanguageCode}
        onLogout={logout}
      />
      <Sidebar logoutCallback={logout} />

      <Box
        marginTop={mgprisHeaderHeight}
        sx={{
          flexGrow: 1,
          p: 0,
          minHeight: `calc(100dvh - ${mgprisHeaderHeight})`,
          marginLeft: 0,
          background: ({ palette }) => palette.grey[100],
          [breakpoints.up('sm')]: {
            marginLeft: 3,
          },
        }}
      >
        {children}
      </Box>
      {postId && (
        <Suspense>
          <SocialFeedPostModal postId={postId} commentKey={commentKey} onClose={closePost} />
        </Suspense>
      )}
    </Box>
  );
}
